import axios from 'axios';
import {
  showAxiosErrorBanner,
  showNetworkErrorBanner,
} from './appErrorBanner';

// ─── Module-level token store ──────────────────────────────────────
// AuthContext calls setAccessToken() whenever the token changes.
// The request interceptor reads it on every outgoing request.
let _accessToken: string | null = null;

/** Called by AuthContext whenever the access token changes. */
export function setAccessToken(token: string | null) {
  _accessToken = token;
}

/** Read-only access for code that needs the raw token (e.g. EventSource headers). */
export function getAccessToken(): string | null {
  return _accessToken;
}

// ─── User context for error presentation ──────────────────────────
// Set by AuthContext so the error interceptor can show plan-aware messages.
let _userContext: { userPlan?: 'free' | 'pro' | 'enterprise'; usage?: { aiRequestsThisMonth: number; pagesThisMonth: number } } | null = null;

/** Called by AuthContext to keep error banner messaging plan-aware. */
export function setUserContext(ctx: typeof _userContext) {
  _userContext = ctx;
}

/** Read-only access to user context for error presentation outside apiClient. */
export function getUserContext() {
  return _userContext;
}

// ─── Logout callback ──────────────────────────────────────────────
// Set by AuthContext so the interceptor can trigger logout on 401.
let _logoutFn: (() => void) | null = null;

/** Called by AuthContext to register the logout function. */
export function setLogoutFn(fn: () => void) {
  _logoutFn = fn;
}

// ─── Axios instance ────────────────────────────────────────────────

const apiClient = axios.create({
  withCredentials: true, // send httpOnly refresh cookie on auth endpoints
});

// ── Request interceptor: auto-attach Bearer token ──────────────────
apiClient.interceptors.request.use((config) => {
  // Don't override Authorization for admin API calls (admin auth manages its own tokens)
  const url = config.url ?? '';
  if (_accessToken && !url.startsWith('/api/admin/')) {
    config.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return config;
});

// ── Response interceptor: 401 auto-logout + global error banner ────
apiClient.interceptors.response.use(
  (response) => response,
  (err) => {
    if (axios.isAxiosError(err)) {
      const cfg = err.config;
      const url = String(cfg?.url ?? '');
      const st = err.response?.status;

      const isAuthEndpoint =
        url.includes('/api/auth/refresh') ||
        url.includes('/api/auth/logout') ||
        url.includes('/api/auth/login') ||
        url.includes('/api/auth/register');
      const isAdminEndpoint = url.includes('/api/admin/');

      // Auto-logout: 401 on a non-auth, non-admin endpoint means the user token is invalid.
      if (st === 401 && !isAuthEndpoint && !isAdminEndpoint && !url.includes('/api/auth/me') && _accessToken) {
        _logoutFn?.();
      }

      // Global error banner (unless caller opted out via skipGlobalErrorBanner).
      if (!cfg?.skipGlobalErrorBanner) {
        const silentAuth = url.includes('/api/auth/refresh') || url.includes('/api/auth/logout');
        if (!(st === 401 && url.includes('/api/auth/me')) && !silentAuth && !isAdminEndpoint) {
          if (st != null) {
            showAxiosErrorBanner(st, err.response?.data, _userContext ?? undefined);
          } else {
            showNetworkErrorBanner();
          }
        }
      }
    }
    return Promise.reject(err);
  },
);

export default apiClient;
